// AI assisted development
/**
 * Admin API Client
 * API functions for team management and engineer-team assignments
 */

import { getAccessToken, isAuthenticated } from './auth';
import type {
  TeamsResponse,
  Team,
  EngineerTeamHistoryResponse,
  EngineerTeamMembership,
  CreateTeamRequest,
  UpdateTeamRequest,
  AssignEngineerToTeamRequest,
  UpdateMembershipRequest,
  FeatureFlagsResponse,
  FeatureFlag,
  UpdateFeatureFlagRequest,
  JiraUsersResponse,
  TeamRepositoriesResponse,
  ServicesResponse,
  RepoServicesResponse,
  UpdateRepoServicesRequest,
  ModuleTeamMappingsResponse,
  ModuleTeamMapping,
  CreateModuleTeamMappingRequest,
  UpdateModuleTeamMappingRequest,
  EligibleManagersResponse,
  RepositoryAutomationMappingsResponse,
  RepositoryAutomationMapping,
  CreateRepositoryAutomationMappingRequest,
  UpdateRepositoryAutomationMappingRequest,
} from '../types/admin';
import type {
  Repository,
  RepositoriesResponse,
  RepositoryAuditLogResponse,
  UpdateRepositorySettingRequest,
  BulkAssignRequest,
  BulkAssignResponse,
} from '../types/repository';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://codereview-dev.azurewebsites.net/api';

/**
 * Helper to make authenticated API requests
 */
async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  if (!isAuthenticated()) {
    throw new Error('User not authenticated');
  }

  const token = await getAccessToken();
  
  // Build headers - only include Authorization if we have a real token
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers as Record<string, string>,
  };
  
  // Only add Authorization header if token is not empty (auth enabled mode)
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed: ${response.statusText}`);
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}

// ============================================================================
// Team Management
// ============================================================================

/**
 * Fetch all teams
 */
export const fetchTeams = async (): Promise<TeamsResponse> => {
  return fetchWithAuth<TeamsResponse>('/management/teams');
};

/**
 * Create a new team
 */
export const createTeam = async (data: CreateTeamRequest): Promise<Team> => {
  return fetchWithAuth<Team>('/management/teams', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Update team name
 */
export const updateTeam = async (teamId: string, data: UpdateTeamRequest): Promise<Team> => {
  return fetchWithAuth<Team>(`/management/teams/${teamId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Delete a team (only if no active members)
 */
export const deleteTeam = async (teamId: string): Promise<void> => {
  return fetchWithAuth<void>(`/management/teams/${teamId}`, {
    method: 'DELETE',
  });
};

/**
 * Fetch eligible team managers (engineers with PRs)
 * Feature UP-48438: Team manager assignment
 */
export const fetchEligibleManagers = async (): Promise<EligibleManagersResponse> => {
  return fetchWithAuth<EligibleManagersResponse>('/management/teams/eligible-managers');
};

export const searchJiraUsers = async (query: string): Promise<JiraUsersResponse> => {
  const params = new URLSearchParams({ query });
  return fetchWithAuth<JiraUsersResponse>(`/management/jira-users?${params.toString()}`);
};

export const adoptExternalProductManager = async (teamId: string): Promise<Team> => {
  return fetchWithAuth<Team>(`/management/teams/${teamId}/product-manager`, {
    method: 'POST',
    body: JSON.stringify({ action: 'adopt_external' }),
  });
};

export const dismissProductManagerStaleAlert = async (teamId: string): Promise<Team> => {
  return fetchWithAuth<Team>(`/management/teams/${teamId}/product-manager`, {
    method: 'POST',
    body: JSON.stringify({ action: 'dismiss_stale' }),
  });
};

// ============================================================================
// Engineer Team Assignment
// ============================================================================

/**
 * Fetch team history for an engineer
 */
export const fetchEngineerTeamHistory = async (
  email: string
): Promise<EngineerTeamHistoryResponse> => {
  return fetchWithAuth<EngineerTeamHistoryResponse>(`/management/engineers/${encodeURIComponent(email)}/teams`);
};

/**
 * Assign an engineer to a team
 */
export const assignEngineerToTeam = async (
  email: string,
  data: AssignEngineerToTeamRequest
): Promise<EngineerTeamMembership> => {
  return fetchWithAuth<EngineerTeamMembership>(
    `/management/engineers/${encodeURIComponent(email)}/teams`,
    {
      method: 'POST',
      body: JSON.stringify(data),
    }
  );
};

/**
 * Update engineer team membership dates
 */
export const updateEngineerTeamMembership = async (
  email: string,
  membershipId: string,
  data: UpdateMembershipRequest
): Promise<EngineerTeamMembership> => {
  return fetchWithAuth<EngineerTeamMembership>(
    `/management/engineers/${encodeURIComponent(email)}/teams/${membershipId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
};

/**
 * Remove engineer team membership
 */
export const removeEngineerTeamMembership = async (
  email: string,
  membershipId: string
): Promise<void> => {
  return fetchWithAuth<void>(
    `/management/engineers/${encodeURIComponent(email)}/teams/${membershipId}`,
    {
      method: 'DELETE',
    }
  );
};

// ============================================================================
// Feature Flag Management
// ============================================================================

/**
 * Fetch all feature flags
 */
export const fetchFeatureFlags = async (): Promise<FeatureFlagsResponse> => {
  return fetchWithAuth<FeatureFlagsResponse>('/management/settings/feature-flags');
};

/**
 * Update a feature flag (creates if doesn't exist)
 */
export const updateFeatureFlag = async (
  name: string,
  enabled: boolean,
  description?: string | null
): Promise<FeatureFlag> => {
  return fetchWithAuth<FeatureFlag>(
    '/management/settings/feature-flags',
    {
      method: 'PUT',
      body: JSON.stringify({ name, enabled, description } as UpdateFeatureFlagRequest),
    }
  );
};

// ============================================================================
// Repository Management (Feature 015)
// ============================================================================

/**
 * Fetch all repositories
 */
export const getRepositories = async (): Promise<RepositoriesResponse> => {
  return fetchWithAuth<RepositoriesResponse>('/management/repositories');
};

/**
 * Get repository by ID and platform
 */
export const getRepository = async (repositoryId: string, platform: string): Promise<Repository> => {
  return fetchWithAuth<Repository>(`/management/repositories/${platform}/${repositoryId}`);
};

/**
 * Update repository settings
 */
export const updateRepositorySetting = async (
  repositoryId: string,
  platform: string,
  settings: UpdateRepositorySettingRequest
): Promise<Repository> => {
  return fetchWithAuth<Repository>(`/management/repositories/${platform}/${repositoryId}`, {
    method: 'PATCH',
    body: JSON.stringify(settings),
  });
};

/**
 * Get repository audit log
 */
export const getRepositoryAuditLog = async (
  repositoryId: string,
  platform: string,
  limit: number = 50
): Promise<RepositoryAuditLogResponse> => {
  return fetchWithAuth<RepositoryAuditLogResponse>(
    `/management/repositories/${platform}/${repositoryId}/audit-log?limit=${limit}`
  );
};

// ============================================================================
// Repository Team Assignment (Feature 016)
// ============================================================================

/**
 * Fetch repositories assigned to a team (T025)
 */
export const fetchTeamRepositories = async (
  teamId: string
): Promise<TeamRepositoriesResponse> => {
  return fetchWithAuth<TeamRepositoriesResponse>(`/management/teams/${teamId}/repositories`);
};

/**
 * Bulk assign repositories to a team (T032)
 */
export const bulkAssignRepositories = async (
  data: BulkAssignRequest
): Promise<BulkAssignResponse> => {
  return fetchWithAuth<BulkAssignResponse>('/management/repositories/bulk-assign', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// ============================================================================
// Service Catalog Management (Feature 020)
// ============================================================================

/**
 * Fetch all discovered services from Datadog
 */
export const fetchAllServices = async (): Promise<ServicesResponse> => {
  return fetchWithAuth<ServicesResponse>('/management/services');
};

/**
 * Fetch services mapped to a repository
 */
export const fetchRepoServices = async (
  repositoryId: string,
  platform: string
): Promise<RepoServicesResponse> => {
  return fetchWithAuth<RepoServicesResponse>(`/management/repositories/${platform}/${repositoryId}/services`);
};

/**
 * Update base service mappings for a repository
 */
export const updateRepoServices = async (
  repositoryId: string,
  platform: string,
  baseServiceIds: string[]
): Promise<void> => {
  return fetchWithAuth<void>(`/management/repositories/${platform}/${repositoryId}/services`, {
    method: 'PUT',
    body: JSON.stringify({ base_service_ids: baseServiceIds } as UpdateRepoServicesRequest),
  });
};

// ============================================================================
// Module-Team Mapping (Feature 024: P1 Incidents and MTTR)
// ============================================================================

/**
 * Fetch all module-to-team mappings
 */
export const fetchModuleTeamMappings = async (): Promise<ModuleTeamMappingsResponse> => {
  return fetchWithAuth<ModuleTeamMappingsResponse>('/management/module-team-mappings');
};

/**
 * Create a module-to-team mapping
 */
export const createModuleTeamMapping = async (
  data: CreateModuleTeamMappingRequest
): Promise<ModuleTeamMapping> => {
  return fetchWithAuth<ModuleTeamMapping>('/management/module-team-mappings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Update a module-to-team mapping
 */
export const updateModuleTeamMapping = async (
  mappingId: string,
  data: UpdateModuleTeamMappingRequest
): Promise<ModuleTeamMapping> => {
  return fetchWithAuth<ModuleTeamMapping>(`/management/module-team-mappings/${mappingId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Delete a module-to-team mapping
 */
export const deleteModuleTeamMapping = async (mappingId: string): Promise<void> => {
  return fetchWithAuth<void>(`/management/module-team-mappings/${mappingId}`, {
    method: 'DELETE',
  });
};

/**
 * Fetch distinct module names from P1 incidents (for autocomplete)
 */
export const fetchDistinctModules = async (): Promise<string[]> => {
  const res = await fetchWithAuth<{ modules: string[] }>('/management/modules');
  return res.modules;
};

export const fetchRepositoryAutomationMappings = async (): Promise<RepositoryAutomationMappingsResponse> => {
  return fetchWithAuth<RepositoryAutomationMappingsResponse>('/management/repository-automation-mappings');
};

export const createRepositoryAutomationMapping = async (
  data: CreateRepositoryAutomationMappingRequest
): Promise<RepositoryAutomationMapping> => {
  return fetchWithAuth<RepositoryAutomationMapping>('/management/repository-automation-mappings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

export const updateRepositoryAutomationMapping = async (
  mappingId: string,
  data: UpdateRepositoryAutomationMappingRequest
): Promise<RepositoryAutomationMapping> => {
  return fetchWithAuth<RepositoryAutomationMapping>(
    `/management/repository-automation-mappings/${mappingId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
};

export const deleteRepositoryAutomationMapping = async (mappingId: string): Promise<void> => {
  return fetchWithAuth<void>(`/management/repository-automation-mappings/${mappingId}`, {
    method: 'DELETE',
  });
};

/**
 * Trigger manual sync of P1 incidents from Jira (Feature 024).
 * POST /api/metrics/p1-sync
 */
export const syncP1Incidents = async (): Promise<{ success: boolean; message?: string }> => {
  return fetchWithAuth<{ success: boolean; message?: string }>('/metrics/p1-sync', {
    method: 'POST',
  });
};

